import { useIntl } from 'react-intl';
import {
  IconAlertOctagon,
  IconCircleCheck,
  IconCircleX,
  IconClockPause,
  IconHelpOctagon,
  IconPlayerPlay,
} from '@rossum/ui/icons/tabler';
import {
  Chip,
  CircularProgress,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { TransformedActivity } from '../../helpers';
import { useRunCancelState } from '../../hooks/useRunCancelState';

export const STATUSES_DATA = {
  running: {
    color: 'info',
    translationKey: 'running',
    Icon: IconPlayerPlay,
  },
  completed: {
    color: 'success',
    translationKey: 'completed',
    Icon: IconCircleCheck,
  },
  failed: {
    color: 'error',
    translationKey: 'failed',
    Icon: IconAlertOctagon,
  },
  cancelled: {
    color: 'default',
    translationKey: 'cancelled',
    Icon: IconCircleX,
  },
  waiting: {
    color: 'aurora',
    translationKey: 'waiting',
    Icon: IconClockPause,
  },
  undefined: {
    color: 'default',
    translationKey: 'undefined',
    Icon: IconHelpOctagon,
  },
} as const;

type StatusColumnProps = TransformedActivity;

export const StatusColumn = (run: StatusColumnProps) => {
  const intl = useIntl();

  const { status, cancelRequested } = run;

  const { isCancelling } = useRunCancelState(run);

  const formattedDateTime = cancelRequested
    ? intl.formatDate(new Date(cancelRequested), {
        dateStyle: 'medium',
        timeStyle: 'medium',
      })
    : '';

  const { color, Icon, translationKey } = STATUSES_DATA[status ?? 'undefined'];

  if (isCancelling) {
    return (
      <Tooltip
        title={
          <Stack>
            <span>
              {intl.formatMessage({
                id: 'features.activities.cancelActivity.cancelingRequestedAt',
              })}
            </span>
            <span>{formattedDateTime}</span>
          </Stack>
        }
      >
        <Chip
          color={STATUSES_DATA.cancelled.color}
          sx={{ color: t => t.palette.text.disabled }}
          icon={<CircularProgress size={12} color="secondary" />}
          label={intl.formatMessage({
            id: `features.activities.status.canceling`,
          })}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      color={color}
      icon={
        <SvgIcon fontSize="medium">
          <Icon />
        </SvgIcon>
      }
      label={intl.formatMessage({
        id: `features.activities.status.${translationKey}`,
      })}
    />
  );
};
